import { NespressoElement } from '@kissui/components/src/nespressoElement'
import { isSafari, isFirefox } from '@kissui/helpers/src/useragent.helpers'
import { debounce } from '@kissui/helpers/src/utils'

// Standard UX delay to prevent accidental hover dismissals
const SEARCH_DISMISS_DELAY = 300
// Light debounce for DOM mutation callbacks to improve performance
const ELEMENT_OBSERVER_DEBOUNCE = 50

class NavEnhance extends NespressoElement {
    constructor() {
        super()
        this.observers = []
        this.eventListeners = []
        this.domContentLoadedHandler = null
        this.timeouts = []
    }

    connectedCallback() {
        super.connectedCallback()
        this.init()
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        this.cleanup()
    }

    cleanup() {
        this.observers.forEach(observer => observer.disconnect())
        this.observers = []

        this.eventListeners.forEach(({ target, type, handler }) => {
            target.removeEventListener(type, handler)
        })
        this.eventListeners = []

        if (this.domContentLoadedHandler) {
            document.removeEventListener('DOMContentLoaded', this.domContentLoadedHandler)
            this.domContentLoadedHandler = null
        }

        this.timeouts.forEach(timeout => clearTimeout(timeout))
        this.timeouts = []
    }

    getObserverOptions() {
        return {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false
        }
    }

    observeElements(observer, selectors) {
        const options = this.getObserverOptions()
        selectors.forEach(selector => {
            const element = document.querySelector(selector)
            if (element) {
                observer.observe(element, options)
            }
        })
    }

    addEventListenerTracked(target, type, handler) {
        target.addEventListener(type, handler)
        this.eventListeners.push({ target, type, handler })
    }

    addTimeoutTracked(callback, delay) {
        const timeout = setTimeout(() => {
            const index = this.timeouts.indexOf(timeout)
            if (index > -1) this.timeouts.splice(index, 1)
            callback()
        }, delay)

        this.timeouts.push(timeout)
        return timeout
    }

    init() {
        // Safari and Firefox detection and class addition
        if (isSafari() || isFirefox()) {
            document.documentElement.classList.add('sns-header-support')
        }

        // Set up burger menu app banner dismissal
        this.setupBurgerMenuDismissal()

        // Move elements to main
        this.setupElementMover()

        // Set up search dismissal functionality
        this.setupSearchDismissal()

        // Set up touch navigation for mobile
        this.setupTouchNavigation()
    }

    setupBurgerMenuDismissal() {
        const handler = function () {
            const burgerSwitchButton = document.querySelector('button.BurgerSwitch')
            if (!burgerSwitchButton) {
                console.warn(
                    '[nav-enhance] BurgerSwitch button not found - burger menu dismissal unavailable'
                )
                return
            }

            burgerSwitchButton.addEventListener('click', () => {
                const appBanner = document.querySelector('nb-app-banner')
                if (appBanner) {
                    appBanner.style.display = 'none'
                } else {
                    console.warn(
                        '[nav-enhance] nb-app-banner not found - banner dismissal unavailable'
                    )
                }
            })
        }
        this.domContentLoadedHandler = handler
        document.addEventListener('DOMContentLoaded', handler)
    }

    setupElementMover() {
        const moveElementToMain = selector => {
            const element = document.querySelector(selector)
            const mainElement = document.querySelector('main#main')
            if (element && mainElement) {
                console.log(`NavEnhance: Moving element ${selector} to main`)
                mainElement.prepend(element)
                return true
            }
            return false
        }

        const targets = [
            '.home-delivery-info-container', // Fallback for elements outside header
            '.nnes-dynamic-sticky-banner'
        ]
        const moved = new Set()
        let elementsFound = 0

        const callback = () => {
            let currentFound = 0
            const movedElements = new Set() // Track actual DOM elements to prevent duplicates

            for (const selector of targets) {
                if (!moved.has(selector)) {
                    const element = document.querySelector(selector)
                    if (element && !movedElements.has(element)) {
                        currentFound++
                        movedElements.add(element)
                        if (moveElementToMain(selector)) {
                            moved.add(selector)
                        }
                    }
                }
            }

            // Update elements found count
            if (currentFound > elementsFound) {
                elementsFound = currentFound
            }

            // Disconnect observer if no new elements are being found and we've moved what we can
            if (moved.size > 0 && currentFound === moved.size) {
                console.log(
                    `NavEnhance: All available elements moved (${moved.size}), disconnecting observer`
                )
                observer.disconnect()
            }
        }

        const debouncedCallback = debounce(callback, ELEMENT_OBSERVER_DEBOUNCE)
        const observer = new MutationObserver(debouncedCallback)

        this.observeElements(observer, ['body', 'header#top', 'main#main'])
        this.observers.push(observer)

        // Run callback immediately to catch any existing elements
        callback()
    }

    setupSearchDismissal() {
        const processedContainers = new Set()

        const waitForElement = (selector, callback) => {
            const checkCallback = () => {
                const element = document.querySelector(selector)
                if (element && !processedContainers.has(element)) {
                    processedContainers.add(element)
                    callback(element)
                }
            }

            const debouncedCallback = debounce(checkCallback, ELEMENT_OBSERVER_DEBOUNCE)
            const observer = new MutationObserver(debouncedCallback)

            this.observeElements(observer, ['header#top'])
            this.observers.push(observer)
        }

        const setupSearchDismissalEvents = searchContainer => {
            const closeButton = searchContainer.querySelector("div[class^='_mobile-bar_'] button")
            if (!closeButton) {
                console.warn(
                    '[nav-enhance] Search close button not found - search dismissal unavailable'
                )
                return
            }

            let hoverTimeout = null

            const dismissHandler = () => {
                closeButton.click()
                processedContainers.delete(searchContainer)
            }

            const delayedDismissHandler = () => {
                if (hoverTimeout) {
                    clearTimeout(hoverTimeout)
                    const index = this.timeouts.indexOf(hoverTimeout)
                    if (index > -1) this.timeouts.splice(index, 1)
                }
                hoverTimeout = this.addTimeoutTracked(dismissHandler, SEARCH_DISMISS_DELAY)
            }

            const cancelDelayedDismiss = () => {
                if (hoverTimeout) {
                    clearTimeout(hoverTimeout)
                    const index = this.timeouts.indexOf(hoverTimeout)
                    if (index > -1) this.timeouts.splice(index, 1)
                    hoverTimeout = null
                }
            }

            this.addEventListenerTracked(searchContainer, 'mouseleave', delayedDismissHandler)
            this.addEventListenerTracked(searchContainer, 'mouseenter', cancelDelayedDismiss)

            const customerArea = document.querySelector('.Header__customer')
            if (customerArea) {
                const customerAreaDismissHandler = event => {
                    // Don't dismiss if clicking inside the search container
                    if (searchContainer.contains(event.target)) {
                        return
                    }
                    dismissHandler()
                }
                this.addEventListenerTracked(customerArea, 'mousedown', customerAreaDismissHandler)
            } else {
                console.warn(
                    '[nav-enhance] Header__customer not found - customer area dismissal unavailable'
                )
            }

            // Close search when hovering over navigation (with delay)
            const navigationNav = document.querySelector('.HeaderNavigationBar__nav')
            if (navigationNav) {
                this.addEventListenerTracked(navigationNav, 'mouseenter', delayedDismissHandler)
                this.addEventListenerTracked(navigationNav, 'mouseleave', cancelDelayedDismiss)
            } else {
                console.warn(
                    '[nav-enhance] HeaderNavigationBar__nav not found - navigation hover dismissal unavailable'
                )
            }
        }

        waitForElement("cv-search-bar div[class^='_container_']", setupSearchDismissalEvents)
    }

    setupTouchNavigation() {
        document.querySelectorAll('.HeaderNavigationBarItem__anchor').forEach(anchor => {
            const touchstartHandler = function (event) {
                if (document.activeElement !== this) {
                    event.preventDefault() // Prevents navigation on the first tap
                    this.focus() // Ensures the element is marked as active
                }
            }
            this.addEventListenerTracked(anchor, 'touchstart', touchstartHandler)
        })
    }
}

customElements.get('nb-nav-enhance') || customElements.define('nb-nav-enhance', NavEnhance)
export default NavEnhance
